import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import FlashMessage from '@/components/FlashMessage';
import Snackbar from '@/components/Snackbar';
import GlobalAlertDialog from '@/components/GlobalAlertDialog';
import ErrorBadge from '@/components/ErrorBadge';
import Spinner from '@/components/Spinner';
import UpdateBanner from '@/components/UpdateBanner';
import ApiDownBanner from '@/components/ApiDownBanner';
import { PageTitleProvider } from '@/contexts/PageTitleContext';
import { CampaignContextSidebar, useShouldShowCampaignSidebar } from '@/components/CampaignContextSidebar';
import { GlobalTableScrollbars } from '@/components/GlobalTableScrollbars';
import { V1CompareButton } from '@/components/V1CompareButton';

const AUTH_PATHS = ['/login', '/forgot_password', '/reset_password', '/change_password'];

// Horizontal scroll inside .ggc-table-frame--scroll containers: drive the scroll
// ourselves and suppress the browser's back/forward navigation gesture.
// (preventDefault alone would block the scroll too, so we apply it manually.)
function useTableScrollGestureLock() {
  useEffect(() => {
    const handler = (e: WheelEvent) => {
      // Only act on primarily-horizontal gestures so vertical page scrolling
      // is never affected.
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      // Find the nearest horizontally-scrollable ancestor (structural, so it
      // covers any table scroll container, not just .ggc-table-frame--scroll).
      let el = e.target instanceof Element ? e.target : null;
      let container: HTMLElement | null = null;
      while (el && el !== document.body) {
        const ox = getComputedStyle(el).overflowX;
        if (ox === 'auto' || ox === 'scroll') { container = el as HTMLElement; break; }
        el = el.parentElement;
      }
      if (!container) return;
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      if (maxScrollLeft <= 0) return;
      // Drive the scroll ourselves and ALWAYS suppress the browser's back/forward
      // navigation — including at the edges, where trackpad momentum/overscroll
      // would otherwise trigger it accidentally.
      container.scrollLeft += e.deltaX;
      e.preventDefault();
    };
    document.addEventListener('wheel', handler, { passive: false });
    return () => document.removeEventListener('wheel', handler);
  }, []);
}

// Pages that render their own ResizableSidebar full-height layout
// (campaigns-layout / dashboard-layout / finances-layout / user-profile-layout)
// host the footer inside their own main column. Suppress the global footer for
// them so it isn't duplicated; everywhere else the footer lives in the content
// column rendered by AppContent below.
function isSelfManagedFooterPage(pathname: string): boolean {
  if (pathname === '/campaigns' || pathname === '/influencers') return true;
  if (pathname.startsWith('/dashboard/campaign/')) return true;
  if (pathname.startsWith('/rosters')) return true;
  if (pathname.startsWith('/finances')) return true;
  if (/^\/influencer\/[a-f0-9]{24}/.test(pathname)) return true; // user profile
  return false;
}

function AppContent() {
  const showSidebar = useShouldShowCampaignSidebar();
  const { pathname } = useLocation();
  const ownFooter = isSelfManagedFooterPage(pathname);
  return (
    <main className={`main-content app-layout__content${showSidebar ? ' campaign-ctx-layout' : ''}`}>
      <Spinner />
      {showSidebar && <CampaignContextSidebar />}
      <div className={showSidebar ? 'campaign-ctx-content' : 'app-content-col'}>
        <Outlet />
        {!ownFooter && <Footer />}
      </div>
      <Snackbar />
      <GlobalAlertDialog />
      <ErrorBadge />
      <GlobalTableScrollbars />
      {/* Admin-only v1↔v2 compare helper; self-gates by role/preview/route. */}
      <V1CompareButton />
    </main>
  );
}

function App() {
  useTableScrollGestureLock();
  const location = useLocation();
  const isAuthPage = AUTH_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));

  if (isAuthPage) {
    return (
      <PageTitleProvider>
        <div className="app-layout">
          <Header />
          <main className="auth-content app-layout__content">
            <Outlet />
            <Footer />
          </main>
        </div>
      </PageTitleProvider>
    );
  }

  return (
    <PageTitleProvider>
      <div className="app-layout">
        <Header />
        <ApiDownBanner />
        <UpdateBanner />
        <FlashMessage />

        <AppContent />
      </div>
    </PageTitleProvider>
  );
}

export default App;
